import { CANONICAL_EVENTS } from "./canonicalEvents";
import { CanonicalUserRole, trackCanonicalEvent } from "./mixpanelTracker";

type RoleInput = "viewer" | "model" | "creator" | "guest" | string | undefined;

function toCanonicalRole(role: RoleInput): CanonicalUserRole {
  if (role === "viewer") return "viewer";
  if (role === "creator" || role === "model") return "creator";
  return "guest";
}

export function trackPageViewed(params: {
  path: string;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  const { path, user_id, user_role } = params;
  let page_type: "landing" | "profile" | "post" | "explore" | "checkout" = "landing";
  if (path.startsWith("/subscriptions")) page_type = "checkout";
  else if (path === "/" || path.startsWith("/dashboard")) page_type = "explore";
  else if (path.startsWith("/post-creator")) page_type = "post";
  else if (path !== "/" && path.split("/").filter(Boolean).length === 1) page_type = "profile";

  trackCanonicalEvent(
    CANONICAL_EVENTS.PAGE_VIEWED,
    { page_type, path },
    { user_id, user_role: toCanonicalRole(user_role) }
  );
}

export function trackDashboardViewed(params: {
  dashboard_type: "earnings" | "analytics" | "payouts" | "subscribers";
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.DASHBOARD_VIEWED,
    { dashboard_type: params.dashboard_type },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackContentViewed(params: {
  view_type: "profile" | "post" | "photo" | "video";
  user_id?: string;
  creator_id?: string;
  post_id?: string;
  content_type?: "photo" | "video" | "mixed";
  watch_time_seconds?: number;
  user_role?: RoleInput;
  mvp_feature?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.CONTENT_VIEWED,
    {
      mvp_feature: params.mvp_feature ?? `content_${params.view_type}`,
      view_type: params.view_type,
      creator_id: params.creator_id,
      post_id: params.post_id,
      content_type: params.content_type,
      watch_time_seconds: params.watch_time_seconds,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackCheckoutStarted(params: {
  purchase_type: "subscription" | "ppv" | "tip" | "custom_video";
  value?: number;
  creator_id?: string;
  post_id?: string;
  user_id?: string;
  user_role?: RoleInput;
  mvp_feature?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.CHECKOUT_STARTED,
    {
      mvp_feature: params.mvp_feature ?? "checkout",
      purchase_type: params.purchase_type,
      value: params.value,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackShareGenerated(params: {
  share_target: "profile" | "post" | "referral";
  share_format: "link" | "qr";
  share_method: "copy_link" | "native_share" | "qr_download";
  user_id?: string;
  creator_id?: string;
  post_id?: string;
  user_role?: RoleInput;
  /** Людсько-читабельний ярлик для фільтрів у Mixpanel (напр. share_post). */
  mvp_feature?: string;
  /** Де саме відбувся share (post_modal, profile_menu, feed_card, тощо). */
  surface?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SHARE_GENERATED,
    {
      mvp_feature: params.mvp_feature ?? `share_${params.share_target}`,
      share_target: params.share_target,
      share_format: params.share_format,
      share_method: params.share_method,
      surface: params.surface,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackShareClicked(params: {
  share_target: "profile" | "post";
  user_id?: string;
  creator_id?: string;
  post_id?: string;
  user_role?: RoleInput;
  mvp_feature?: string;
  surface?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SHARE_CLICKED,
    {
      mvp_feature: params.mvp_feature ?? `share_${params.share_target}_intent`,
      share_target: params.share_target,
      surface: params.surface,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackSignupStarted(params: {
  path?: string;
  user_role?: RoleInput;
  signup_method?: string;
  source?: string;
  medium?: string;
  campaign?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SIGNUP_STARTED,
    {
      mvp_feature: "auth_register",
      path: params.path,
      signup_method: params.signup_method,
      source: params.source,
      medium: params.medium,
      campaign: params.campaign,
    },
    { user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackSignupCompleted(params: {
  user_id?: string;
  user_role?: RoleInput;
  signup_method: string;
  source?: string;
  medium?: string;
  campaign?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SIGNUP_COMPLETED,
    {
      mvp_feature: "auth_register",
      signup_method: params.signup_method,
      source: params.source,
      medium: params.medium,
      campaign: params.campaign,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackEmailClicked(params: {
  email_type: "activation" | "reengagement" | "notification";
  user_id?: string;
  destination_type?: string;
  creator_id?: string;
  post_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.EMAIL_CLICKED,
    {
      mvp_feature: "email",
      email_type: params.email_type,
      destination_type: params.destination_type,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: "guest" }
  );
}

export function trackReferralAttributedStage(params: {
  referral_stage: "visit" | "signup" | "activation" | "conversion";
  referrer_user_id?: string;
  creator_id?: string;
  post_id?: string;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.REFERRAL_ATTRIBUTED,
    {
      mvp_feature: "referral",
      referral_stage: params.referral_stage,
      referrer_user_id: params.referrer_user_id,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackContentUploadStarted(params: {
  content_type: "photo" | "video" | "gallery";
  is_first_upload: boolean;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.CONTENT_UPLOAD_STARTED,
    {
      mvp_feature: "post_creator",
      content_type: params.content_type,
      is_first_upload: params.is_first_upload,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackContentUploadCompleted(params: {
  content_type: "photo" | "video" | "gallery";
  is_first_upload: boolean;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.CONTENT_UPLOAD_COMPLETED,
    {
      mvp_feature: "post_creator",
      content_type: params.content_type,
      is_first_upload: params.is_first_upload,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackPostPublished(params: {
  is_first_post: boolean;
  content_type: "photo" | "video" | "mixed";
  visibility_type: "free" | "subscriber" | "ppv";
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.POST_PUBLISHED,
    {
      mvp_feature: "post_creator",
      is_first_post: params.is_first_post,
      content_type: params.content_type,
      visibility_type: params.visibility_type,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackMessageSent(params: {
  user_id?: string;
  user_role?: RoleInput;
  is_paid_message: boolean;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.MESSAGE_SENT,
    {
      mvp_feature: "messages",
      message_type: "dm",
      is_paid_message: params.is_paid_message,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackFeedEngaged(params: {
  feed_type: "explore" | "following" | "recommended";
  scroll_depth: number;
  items_seen: number;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.FEED_ENGAGED,
    {
      mvp_feature: "home_feed",
      feed_type: params.feed_type,
      scroll_depth: params.scroll_depth,
      items_seen: params.items_seen,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackOfferViewed(params: {
  offer_type: "ppv" | "subscription" | "custom_video";
  creator_id?: string;
  post_id?: string;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.OFFER_VIEWED,
    {
      mvp_feature: "monetization",
      offer_type: params.offer_type,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackNotificationClicked(params: {
  notification_type: string;
  destination_type?: string;
  user_id?: string;
  post_id?: string;
  creator_id?: string;
  user_role?: RoleInput;
  notification_id?: string;
  /** Перевизначення дефолтного mvp_feature ("in_app_notifications") */
  mvp_feature?: string;
  surface?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.NOTIFICATION_CLICKED,
    {
      mvp_feature: params.mvp_feature ?? "in_app_notifications",
      notification_type: params.notification_type,
      destination_type: params.destination_type,
      post_id: params.post_id,
      creator_id: params.creator_id,
      notification_id: params.notification_id,
      surface: params.surface,
    },
    {
      user_id: params.user_id,
      user_role: toCanonicalRole(params.user_role),
    }
  );
}

export function trackSubscriptionPlanCreated(params: {
  is_first_plan: boolean;
  plan_type: "free" | "premium";
  tier_number: 0 | 1 | 2 | 3;
  price?: number;
  billing_period?: string;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SUBSCRIPTION_PLAN_CREATED,
    {
      mvp_feature: "subscription_plans",
      is_first_plan: params.is_first_plan,
      plan_type: params.plan_type,
      tier_number: params.tier_number,
      price: params.price,
      billing_period: params.billing_period,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackEmailSent(params: {
  email_type: "activation" | "reengagement" | "notification";
  source?: string;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.EMAIL_SENT,
    {
      mvp_feature: "email",
      email_type: params.email_type,
      source: params.source,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackEmailOpened(params: {
  email_type: "activation" | "reengagement" | "notification";
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.EMAIL_OPENED,
    {
      mvp_feature: "email",
      email_type: params.email_type,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackPaymentCompleted(params: {
  purchase_type: "subscription" | "ppv" | "tip" | "custom_video";
  value: number;
  currency?: string;
  user_id?: string;
  user_role?: RoleInput;
  creator_id?: string;
  post_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.PAYMENT_COMPLETED,
    {
      mvp_feature: "monetization",
      purchase_type: params.purchase_type,
      value: params.value,
      currency: params.currency,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackPaymentFailed(params: {
  purchase_type: "subscription" | "ppv" | "tip" | "custom_video";
  failure_reason?: string;
  value?: number;
  currency?: string;
  user_id?: string;
  user_role?: RoleInput;
  creator_id?: string;
  post_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.PAYMENT_FAILED,
    {
      mvp_feature: "monetization",
      purchase_type: params.purchase_type,
      failure_reason: params.failure_reason,
      value: params.value,
      currency: params.currency,
      creator_id: params.creator_id,
      post_id: params.post_id,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackPaymentFailedPageView(params: {
  has_return_to: boolean;
}): void {
  trackCanonicalEvent(CANONICAL_EVENTS.PAYMENT_FAILED_PAGE_VIEW, {
    mvp_feature: "payment_failed_page",
    has_return_to: params.has_return_to,
  });
}

export function trackSubscriptionStatusChanged(params: {
  subscription_status: "started" | "upgraded" | "renewed" | "cancelled";
  tier_number?: number;
  price?: number;
  user_id?: string;
  creator_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.SUBSCRIPTION_STATUS_CHANGED,
    {
      mvp_feature: "subscriptions",
      subscription_status: params.subscription_status,
      tier_number: params.tier_number,
      price: params.price,
      creator_id: params.creator_id,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}

export function trackCustomRequestStatusChanged(params: {
  request_status:
    | "created"
    | "viewed"
    | "accepted"
    | "declined"
    | "fulfillment_started"
    | "in_progress"
    | "delivered"
    | "completed";
  creator_id?: string;
  value?: number;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.CUSTOM_REQUEST_STATUS_CHANGED,
    {
      mvp_feature: "custom_requests",
      request_type: "custom_video",
      request_status: params.request_status,
      creator_id: params.creator_id,
      value: params.value,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackRefundStatusChanged(params: {
  refund_status: "requested" | "completed";
  purchase_type?: "subscription" | "ppv" | "tip" | "custom_video";
  value?: number;
  currency?: string;
  user_id?: string;
  user_role?: RoleInput;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.REFUND_STATUS_CHANGED,
    {
      mvp_feature: "refunds",
      refund_status: params.refund_status,
      purchase_type: params.purchase_type,
      value: params.value,
      currency: params.currency,
    },
    { user_id: params.user_id, user_role: toCanonicalRole(params.user_role) }
  );
}

export function trackPayoutStatusChanged(params: {
  payout_status: "requested" | "completed";
  creator_id?: string;
  value?: number;
  currency?: string;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    CANONICAL_EVENTS.PAYOUT_STATUS_CHANGED,
    {
      mvp_feature: "payouts",
      payout_status: params.payout_status,
      creator_id: params.creator_id,
      value: params.value,
      currency: params.currency,
    },
    { user_id: params.user_id, user_role: "creator" }
  );
}
